import React from 'react'
import {useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {intlFormatDistance} from 'date-fns/intlFormatDistance'
import {getLanguageCode} from 'modules/meta/selectors'

import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {NoStyleLink} from '@vio/atlas/helpers/NoStyleLink'
import mirror from '@vio/atlas/src/utils/mirror'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {linariaMq} from '@vio/atlas/utils/breakpoints'
import {TrustPilotReview} from '@vio/core/src/trustpilotWidget/types'

const ReviewCard = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  padding: ${cssTheme.layout.spacing.s600};
  width: 272px;
  height: 328px;
  background-color: ${cssTheme.colors.background.neutral.primary};
  box-shadow: ${cssTheme.shadows.fixed};
  border-radius: ${cssTheme.layout.radius.xxl};

  justify-content: space-between;
  align-items: flex-start;

  ${linariaMq.desktopXs} {
    width: 320px;
    height: 356px;
  }
`

const ReviewText = styled(Text)`
  padding-top: ${cssTheme.layout.spacing.s600};
  padding-left: ${cssTheme.layout.spacing.s600};
  line-clamp: 6;
  -webkit-line-clamp: 6;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

const ReviewRate = styled.img`
  width: 120px;
`

const ConsumerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: ${cssTheme.layout.spacing.s600};
  gap: ${cssTheme.layout.spacing.s300};
`

const QuotationMarkWrapper = styled.div`
  position: absolute;
  top: ${cssTheme.layout.spacing.s700};
  left: ${cssTheme.layout.spacing.s600};
`

const QuotationMarkSvg = styled.svg`
  ${mirror()}
  fill: ${cssTheme.colors.content.neutral.secondary};
`

const QuotationMark = () => (
  <QuotationMarkSvg
    width="16"
    height="13"
    viewBox="0 0 16 13"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M15.328 10.04C15.328 11.624 13.984 12.92 12.208 12.92C10.336 12.92 8.704 11.576 8.704 8.84C8.704 3.176 12.4 0.727998 15.088 0.439999V2.696C12.976 3.176 11.536 5.384 11.488 7.352C11.632 7.256 11.968 7.112 12.448 7.112C14.032 7.112 15.328 8.216 15.328 10.04ZM7.12 10.04C7.12 11.624 5.776 12.92 4 12.92C2.128 12.92 0.496 11.576 0.496 8.84C0.496 3.176 4.192 0.727998 6.88 0.439999V2.696C4.768 3.176 3.328 5.384 3.28 7.352C3.424 7.256 3.76 7.112 4.24 7.112C5.824 7.112 7.12 8.216 7.12 10.04Z" />
  </QuotationMarkSvg>
)

interface Props {
  review: TrustPilotReview
}
export const TrustPilotReviewItem: React.FC<Props> = ({review}) => {
  const {isMobile} = useDeviceLayout()
  const languageCode = useSelector(getLanguageCode)

  return (
    <NoStyleLink href={review.reviewLink} target="_blank">
      <ReviewCard>
        <QuotationMarkWrapper>
          <QuotationMark />
        </QuotationMarkWrapper>
        <ReviewText variant={isMobile ? 'bodyM' : 'bodyL'}>
          {review.text}
        </ReviewText>
        <ConsumerWrapper>
          <ReviewRate
            src={`https://images-static.trustpilot.com/api/stars/${review.stars}/star.svg`}
            alt="review-rate"
          />
          <Text
            variant={isMobile ? 'bodyS' : 'bodyM'}
            colorPath="content.neutral.primary"
          >
            {review.consumer.displayName} ,{' '}
            {intlFormatDistance(new Date(review.createdAt), new Date(), {
              locale: languageCode,
              numeric: 'always'
            })}
          </Text>
        </ConsumerWrapper>
      </ReviewCard>
    </NoStyleLink>
  )
}
